import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { useId } from "react";

import {
  buildLoaderCss,
  className,
  GAP,
  LOGO_SHAPES,
  shapeCenter,
  VIEW_BOX,
} from "./pimo-loader-engine";
import { PIMO_LOADER_MOTIONS, PimoLoaderVariant } from "./pimo-loader-variants";

export type { PimoLoaderVariant } from "./pimo-loader-variants";

export interface PimoLoaderProps {
  /**
   * - `loading`: the logo turns once as a carousel (default)
   * - `generating`: the shapes hop around and remix the logo's colours
   * - `starting`: the logo assembles from its centre and folds away again
   * - `thinking`: a wave runs through the shapes
   */
  variant?: PimoLoaderVariant;
  /** width and height, as a number of pixels or any CSS length */
  size?: number | string;
  /** accessible name, read by screen readers */
  label?: string;
}

const cssCache = new Map<PimoLoaderVariant, string>();

function loaderCss(variant: PimoLoaderVariant) {
  let css = cssCache.get(variant);
  if (!css) {
    css = buildLoaderCss(variant, PIMO_LOADER_MOTIONS[variant]);
    cssCache.set(variant, css);
  }
  return css;
}

const LAYERS = LOGO_SHAPES.map((_, layer) => layer);

export const PimoLoader: PimoReactComponent<PimoLoaderProps> = ({
  variant = "loading",
  size = 64,
  label = "Loading",
}) => {
  // mask ids must be unique per instance; useId may contain characters invalid in url(#…)
  const maskId = `pimo-loader-${useId().replace(/[^a-zA-Z0-9-]/g, "")}`;
  const { x, y, size: side } = VIEW_BOX;

  const renderLayer = (layer: number, knockout = false) =>
    LOGO_SHAPES.map(({ name, color, path }) => {
      const [cx, cy] = shapeCenter(name);
      return (
        <g key={name} className={className(variant, name, layer)}>
          <path
            transform={`translate(${-cx} ${-cy})`}
            d={path}
            {...(knockout
              ? {
                  fill: "#000",
                  stroke: "#000",
                  strokeWidth: GAP * 2,
                  strokeLinejoin: "round" as const,
                }
              : { fill: color })}
          />
        </g>
      );
    });

  return (
    <svg
      width={size}
      height={size}
      viewBox={`${x} ${y} ${side} ${side}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={label}
    >
      <style>{loaderCss(variant)}</style>
      <defs>
        {/* layer k is masked by every layer in front of it, each grown by GAP */}
        {LAYERS.slice(0, -1).map((layer) => (
          <mask
            key={layer}
            id={`${maskId}-${layer}`}
            maskUnits="userSpaceOnUse"
            x={x}
            y={y}
            width={side}
            height={side}
          >
            <rect x={x} y={y} width={side} height={side} fill="#fff" />
            {LAYERS.slice(layer + 1).map((front) => (
              <g key={front}>{renderLayer(front, true)}</g>
            ))}
          </mask>
        ))}
      </defs>
      {LAYERS.map((layer) => (
        <g
          key={layer}
          mask={
            layer < LAYERS.length - 1 ? `url(#${maskId}-${layer})` : undefined
          }
        >
          {renderLayer(layer)}
        </g>
      ))}
    </svg>
  );
};

export default PimoLoader;
